'use client';

import * as React from 'react';
import {
  usePmOnboardingSession,
  type PmOnboardingSession,
  PmOnboardingStepKey,
} from '@hooks';

export { PmOnboardingStepKey };

export interface PmOnboardingSessionContextValue {
  session: PmOnboardingSession | null;
  loading: boolean;
  error: Error | null;
  notFound: boolean;
  completeStep: (
    stepKey: PmOnboardingStepKey,
    sessionIdOverride?: string
  ) => Promise<PmOnboardingSession | null>;
  navigateToStep: (
    stepKey: PmOnboardingStepKey,
    sessionIdOverride?: string
  ) => Promise<PmOnboardingSession | null>;
  refetch: () => Promise<unknown>;
}

const PmOnboardingSessionContext =
  React.createContext<PmOnboardingSessionContextValue | null>(null);

export function usePmOnboardingSessionContext(): PmOnboardingSessionContextValue {
  const context = React.useContext(PmOnboardingSessionContext);
  if (!context) {
    throw new Error(
      'usePmOnboardingSessionContext must be used within PmOnboardingSessionProvider'
    );
  }
  return context;
}

interface PmOnboardingSessionProviderProps {
  children: React.ReactNode;
}

export function PmOnboardingSessionProvider({
  children,
}: PmOnboardingSessionProviderProps) {
  const {
    session,
    loading,
    error,
    notFound,
    completeStep,
    navigateToStep,
    refetch,
  } = usePmOnboardingSession();

  const value = React.useMemo(
    () => ({
      session,
      loading,
      error,
      notFound,
      completeStep,
      navigateToStep,
      refetch,
    }),
    [session, loading, error, notFound, completeStep, navigateToStep, refetch]
  );

  return (
    <PmOnboardingSessionContext.Provider value={value}>
      {children}
    </PmOnboardingSessionContext.Provider>
  );
}
